import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient, ApolloLink, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { HttpLink } from '@apollo/client/link/http';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import Constants from './utils/Constants';
import { createLogger } from './utils/logging';
import store from './configureStore';
import {
    getAccessToken,
    getWebSocketErrorSentTime,
} from './reducers';
import { getUserIdFromToken } from './utils/authentication';
import {
    removeWebSocketErrorSentTime,
    sendWebSocketErrorNotification,
} from './actions/configuration';

const logger = createLogger('CreateApolloClient');

function createNewApolloClient() { // NOSONAR javascript:S3776

    const httpLink = new HttpLink({
        uri: `${Constants.IS_HTTPS ? 'https' : 'http'}://${process.env.REACT_APP_SERVER_HOST || window.location.host}${Constants.GRAPHQL_PATH}`,
        credentials: 'same-origin',
    });
    const authLink = setContext((request, previousContext) => {
        const token = getAccessToken(store.getState());
        return {
            headers: {
                ...previousContext.headers,
                Authorization: token ? `${Constants.BEARER_PREFIX} ${token}` : '',
            },
        };
    });

    const wsClient = new SubscriptionClient(`${Constants.IS_HTTPS ? 'wss' : 'ws'}://${process.env.REACT_APP_SERVER_HOST || window.location.host}${Constants.GRAPHQL_WS_PATH}`, {
        // WARNING!
        // If changed, don't forget to check the "graphql.spqr.ws.keep-alive.interval-millis" property on Backend!
        // Use the higher timeout to avoid possible race conditions causing unwanted client re-connection and stale websocket.
        // Default timeout on Frontend is 30000ms which causes client re-connection when Keep Alive message was not received.
        timeout: 65000,

        reconnect: true,
        lazy: true,
        inactivityTimeout: Constants.WS_INACTIVITY_TIMEOUT_IN_SECS * 1000,
        connectionParams: () => ({
            Authorization: getAccessToken(store.getState()),
        }),
    });
    wsClient.onError(() => {
        const webSocketErrorSentTime = getWebSocketErrorSentTime(store.getState());
        if (!webSocketErrorSentTime) {
            logger.error(`WebSocket error occured for user: ${getUserIdFromToken(getAccessToken(store.getState()))}`);
            store.dispatch(sendWebSocketErrorNotification(Date.now()));
        }
    });
    wsClient.onConnected(() => {
        store.dispatch(removeWebSocketErrorSentTime());
        logger.warn(`WebSocket connected for user: ${getUserIdFromToken(getAccessToken(store.getState()))}`);
    });
    wsClient.onReconnected(() => {
        store.dispatch(removeWebSocketErrorSentTime());
        logger.warn(`WebSocket reconnected for user: ${getUserIdFromToken(getAccessToken(store.getState()))}`);
    });

    const wsLink = new WebSocketLink(wsClient);
    const subscriptionMiddleware = {
        applyMiddleware(options, next) {
            options.Authorization = getAccessToken(store.getState()); // eslint-disable-line no-param-reassign
            next();
        },
    };
    wsLink.subscriptionClient.use([subscriptionMiddleware]);

    const link = split(
        // split based on operation type
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === 'OperationDefinition'
                && definition.operation === 'subscription'
            );
        },
        wsLink,
        authLink.concat(httpLink),
    );

    return new ApolloClient({
        link: ApolloLink.from([
            onError(({ graphQLErrors }) => {
                if (graphQLErrors) {
                    graphQLErrors.map(({ message, locations, path }) => logger.error(
                        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                    ));
                }
            }),
            new RetryLink({
                attempts: (count, operation, error) => {
                    let result;
                    if (error.statusCode !== 401 && error.statusCode !== 403) {
                        const queryNames = operation.query.definitions.map((queryDefinition) => queryDefinition.name.value).join(', ');
                        if (count <= Constants.MAX_RETRIES_WHEN_NETWORK_ERROR) {
                            logger.error(`Retrying (count: ${count}): ${queryNames}, because of error: ${error}`);
                            result = true;
                        } else {
                            logger.error(`STOP retrying of ${queryNames}, because of error: ${error}`);
                            result = false;
                        }
                    } else {
                        result = false;
                        logger.error('Unauthorized access, no retry!');
                    }
                    return result;
                },
            }),
            link,
        ]),
        cache: new InMemoryCache(),
    });

}

let apolloClient = null;
export default function createApolloClient() {
    if (apolloClient === null) {
        apolloClient = createNewApolloClient();
    }
    return apolloClient;
}
